import {
  STAFF_CATALOG_BOUNDS,
  type StaffCatalogUploadContentType,
  staffCatalogUploadContentTypes,
} from "@lafayette/schemas";

// One image upload as a small state machine. The flow (design section 3):
//   requesting  -> ask the API for a signed upload target (W10)
//   uploading   -> send the file straight to storage
//   verifying   -> attach the new path with a media save (W9); the server inspects the object
//   attached    -> the item now holds the file; failed -> nothing was attached, no row exists
// The reducer is pure. The component runs the effect of each state and sends the next event.

export type UploadStage = "requesting" | "uploading" | "verifying";
export type UploadFailureStage = UploadStage | "rejected";

export type UploadState =
  | Readonly<{
      status: "requesting" | "uploading" | "verifying" | "attached";
      id: string;
      fileName: string;
      byteSize: number;
      contentType: StaffCatalogUploadContentType;
      storageObjectPath: string | null;
    }>
  | Readonly<{
      status: "failed";
      id: string;
      fileName: string;
      byteSize: number;
      contentType: StaffCatalogUploadContentType | null;
      storageObjectPath: string | null;
      failedAt: UploadFailureStage;
      message: string;
      retryable: boolean;
    }>;

export type UploadEvent =
  | Readonly<{ type: "target_received"; storageObjectPath: string }>
  | Readonly<{ type: "uploaded" }>
  | Readonly<{ type: "attached" }>
  | Readonly<{ type: "failed"; message: string; retryable: boolean }>
  | Readonly<{ type: "retry" }>;

export type FileCheck =
  | Readonly<{ ok: true; contentType: StaffCatalogUploadContentType }>
  | Readonly<{ ok: false; message: string }>;

const MAX_MEGABYTES = STAFF_CATALOG_BOUNDS.uploadBytes / (1024 * 1024);

/** The checks the browser can do before it asks for a target. The server checks again (magic bytes too). */
export function checkFile(file: Readonly<{ type: string; size: number }>): FileCheck {
  const contentType = staffCatalogUploadContentTypes.find((type) => type === file.type);
  if (contentType === undefined) {
    return { ok: false, message: "Only JPEG, PNG and WebP images can be uploaded." };
  }
  if (file.size < 1) return { ok: false, message: "The file is empty." };
  if (file.size > STAFF_CATALOG_BOUNDS.uploadBytes) {
    return { ok: false, message: `The file is larger than ${MAX_MEGABYTES} MB.` };
  }
  return { ok: true, contentType };
}

/** The first state of a file. A file the browser refuses never reaches the API. */
export function startUpload(
  id: string,
  file: Readonly<{ name: string; type: string; size: number }>,
): UploadState {
  const check = checkFile(file);
  if (!check.ok) {
    return {
      status: "failed",
      id,
      fileName: file.name,
      byteSize: file.size,
      contentType: null,
      storageObjectPath: null,
      failedAt: "rejected",
      message: check.message,
      retryable: false,
    };
  }
  return {
    status: "requesting",
    id,
    fileName: file.name,
    byteSize: file.size,
    contentType: check.contentType,
    storageObjectPath: null,
  };
}

/** A failed upload restarts at the stage that failed. A refused file cannot be retried. */
export function retryStage(state: UploadState): UploadStage | null {
  if (state.status !== "failed" || !state.retryable || state.failedAt === "rejected") return null;
  if (state.failedAt === "verifying" && state.storageObjectPath !== null) return "verifying";
  return "requesting";
}

export function uploadReducer(state: UploadState, event: UploadEvent): UploadState {
  switch (event.type) {
    case "target_received":
      return state.status === "requesting"
        ? { ...state, status: "uploading", storageObjectPath: event.storageObjectPath }
        : state;
    case "uploaded":
      return state.status === "uploading" ? { ...state, status: "verifying" } : state;
    case "attached":
      return state.status === "verifying" ? { ...state, status: "attached" } : state;
    case "failed": {
      if (state.status === "failed" || state.status === "attached") return state;
      return {
        status: "failed",
        id: state.id,
        fileName: state.fileName,
        byteSize: state.byteSize,
        contentType: state.contentType,
        storageObjectPath: state.storageObjectPath,
        failedAt: state.status,
        message: event.message,
        retryable: event.retryable,
      };
    }
    case "retry": {
      const stage = retryStage(state);
      if (stage === null || state.status !== "failed" || state.contentType === null) return state;
      return {
        status: stage,
        id: state.id,
        fileName: state.fileName,
        byteSize: state.byteSize,
        contentType: state.contentType,
        // A retry of the attach keeps the path (the command hook reuses its idempotency key, so the server runs the save once). A retry of an
        // earlier stage starts over with a new target.
        storageObjectPath: stage === "verifying" ? state.storageObjectPath : null,
      };
    }
  }
}

/** A short status for a screen reader and for the row. */
export function describeUpload(state: UploadState): string {
  switch (state.status) {
    case "requesting":
      return "Preparing the upload";
    case "uploading":
      return "Uploading";
    case "verifying":
      return "Checking the file";
    case "attached":
      return "Added";
    case "failed":
      return `Failed: ${state.message}`;
  }
}

export function isUploadActive(state: UploadState): boolean {
  return (
    state.status === "requesting" || state.status === "uploading" || state.status === "verifying"
  );
}
